/**
 * GAP2-09 (#216): count the exits.
 *
 * The audit found 1,824 outbound links to the owner's other properties and to Amazon, carrying no
 * campaign parameter, no click handler and no GTM trigger between them. That absence is why the
 * whole problem survived: nobody could answer "how many people click the Amazon icon?", so there
 * was never a number to argue with, and every proposed cut was a matter of opinion.
 *
 * This module supplies the number. One delegated listener, one dataLayer push per qualifying
 * click, no network request of its own.
 *
 * WHY dataLayer AND NOT gtag OR A BEACON. `snippets/woopf-consent-gate.liquid` creates
 * `window.dataLayer` synchronously and then boots the GTM container only once Shopify's Customer
 * Privacy API allows analytics processing. Pushing to that array is therefore consent-respecting
 * by construction: the push always happens, and nothing leaves the browser unless the container
 * booted. A `navigator.sendBeacon` would go out regardless, which is exactly the behaviour PRIV-01
 * was fixed to stop.
 *
 * KNOWN LIMIT, AND IT IS THE INTERESTING ONE. A same-tab exit may be lost: the push lands in the
 * array, but if the container has not flushed before unload the event never reaches GA4. Every
 * destination here opens in a new tab except the announcement bar, which has no target at all
 * (GAP2-02, #209) - so the one link this cannot reliably measure is the one whose own finding says
 * it should be opening a new tab anyway. Fixing #209 fixes the measurement gap as a side effect.
 * Do not "solve" it here by delaying navigation on click; a tracker that makes the site feel slow
 * gets removed, and it would be measuring the delay it introduced.
 */

/**
 * Registrable hosts whose traffic this exists to count. Matched as an exact host or a subdomain
 * of one, never as a substring: "amazon.com.au" must not be found inside "notamazon.com.au.co".
 */
export const WATCHED_HOSTS = [
  'amazon.com.au',
  'amazon.com',
  'askavet.com',
  'purrz.com',
  'xtallion.com',
  'apps.apple.com',
  'play.google.com',
  'r2.dev', // the 226 MB catalogue PDF in the footer (TAG-11, #156)
];

const isWatched = (host) => WATCHED_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));

/**
 * The payload for one click, or null if this link is not an exit worth counting.
 *
 * Exported and pure so the matching can be tested without a DOM: the suite runs in node.
 *
 * @param {string} href     the link's resolved href
 * @param {string} text     its visible text
 * @param {string} ownHost  the storefront's own host, so an internal link is never counted
 */
export function describeExit(href, text, ownHost) {
  let url;
  try {
    url = new URL(href);
  } catch {
    return null; // mailto:, tel:, #anchor, javascript:, or nothing at all
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  if (url.hostname === ownHost) return null;
  if (!isWatched(url.hostname)) return null;

  return {
    event: 'outbound_brand_exit',
    link_domain: url.hostname,
    link_url: url.href,
    link_text: (text || '').replace(/\s+/g, ' ').trim().slice(0, 100),
  };
}

/**
 * The same link with campaign parameters on it, or unchanged if it already carries any.
 *
 * GAP2-09 (#216). A corpus-wide grep for `utm_` across all 217 pages returned zero matches: not
 * one of the 1,824 outbound brand links carried a campaign parameter, so a visitor who left for
 * askavet.com, purrz.com or Amazon was invisible on both sides. The owner could not answer "how
 * many people click the Amazon icon" or "does the announcement bar send more people to purrz than
 * it keeps", which is why the leak survived unexamined.
 *
 * STAMPED ON THE CLICK, NOT IN THE MARKUP, and that is the important choice. Campaign parameters
 * written into 1,824 hrefs are campaign parameters Google crawls and indexes on the destination
 * sites, and they would have to be maintained across Theme Editor JSON, Replo pages and app
 * blocks. Here the DOM keeps clean URLs and the campaign exists only on a real human click.
 *
 * An existing `utm_` is left alone: whoever set it meant it. The Play Store link's
 * `pcampaignid=web_share` is not a utm parameter and does not count as one.
 *
 * Exported and pure so it can be tested without a DOM.
 *
 * @param {string} href     the link's resolved href
 * @param {string} source   the storefront's own host, so the destination knows who sent them
 * @param {string} content  where on the page the link was, e.g. the section that holds it
 */
export function stampCampaign(href, source, content) {
  let url;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  // `mailto:` and `tel:` are valid URLs and would otherwise be stamped with a query string.
  // `describeExit` already filters those out before the caller gets here, but this is exported
  // and tested on its own, so it does not rely on being called correctly.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return href;
  for (const key of url.searchParams.keys()) {
    if (key.startsWith('utm_')) return href;
  }
  url.searchParams.set('utm_source', source || 'woopf.com');
  url.searchParams.set('utm_medium', 'referral');
  url.searchParams.set('utm_campaign', 'brand_exit');
  if (content) url.searchParams.set('utm_content', content);
  return url.href;
}

/**
 * Where on the page the link sits: the Shopify section that holds it, with the generated id
 * prefix stripped, else the page type.
 *
 * This is the half that answers the owner's actual question. "1,824 exits" is a number nobody can
 * act on; "the announcement bar sent 400 and the footer 30" is a decision.
 */
export function placementOf(anchor, fallback) {
  let section;
  try {
    section = anchor?.closest?.('[id^="shopify-section-"]');
  } catch {
    // This runs inside a click handler on every link on the site. Nothing here is worth an
    // exception: a DOM that cannot answer the question just means the fallback. Found by the
    // GAP4-10 (#340) interaction test, whose deliberately small fake page throws on selectors it
    // does not implement.
    return fallback;
  }
  const id = section?.id?.replace(/^shopify-section-/, '');
  if (!id) return fallback;
  // Theme Editor ids carry a generated suffix, e.g. `sections--1234__announcement-bar`.
  return id.replace(/^sections--\d+__/, '').slice(0, 60);
}

/**
 * Shopify's page type, taken from the body class the layout already writes
 * (`template-product`, `template-index`, ...). Read from the DOM rather than written into an
 * inline script, because `tests/sections/inline-script-budget.test.mjs` guards those bytes and
 * this is worth no new ones.
 */
function pageType(body) {
  const match = /(?:^|\s)template-([a-z0-9-]*)/.exec(body?.className || '');
  const value = match?.[1];
  return value ? value : 'index';
}

function init() {
  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target?.closest?.('a[href]');
      if (!anchor) return;

      const exit = describeExit(anchor.href, anchor.textContent, window.location.hostname);
      if (!exit) return;

      const type = pageType(document.body);

      // Pushed before the href is stamped, so the event records the link as it was authored and
      // a second click on the same anchor reports the same URL as the first.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        ...exit,
        page_type: type,
        page_location: window.location.href,
      });

      // Mutating href during the capture phase is enough: the browser resolves the destination
      // after the event has finished dispatching. The listener stays passive - it never calls
      // preventDefault and so can never be the thing that delays a navigation.
      try {
        anchor.href = stampCampaign(
          anchor.href,
          window.location.hostname,
          placementOf(anchor, type),
        );
      } catch {
        // Best effort, and deliberately so. The exit is already counted above; losing the
        // campaign on one click costs less than throwing inside the handler that runs on every
        // link on the site. An anchor whose href cannot be written navigates as authored.
      }
    },
    // Capture, so a handler that stops propagation further down cannot hide the exit; passive,
    // so this can never be the thing that delays a navigation.
    { capture: true, passive: true },
  );
}

if (typeof document !== 'undefined') init();
